import React from 'react';

import AttractionIcon from './icons/place/location-attraction.svg';
import BeachIcon from './icons/place/location-beach.svg';
import CampsiteIcon from './icons/place/location-campsite.svg';
import CaveIcon from './icons/place/location-cave.svg';
import CityIcon from './icons/place/location-city.svg';
import DogParkIcon from './icons/place/location-dog-park.svg';
import FellIcon from './icons/place/location-fell.svg';
import ForestIcon from './icons/place/location-forest.svg';
import GardenIcon from './icons/place/location-garden.svg';
import GlacierIcon from './icons/place/location-glacier.svg';
import GrassIcon from './icons/place/location-grass.svg';
import GrasslandIcon from './icons/place/location-grassland.svg';
import HeathIcon from './icons/place/location-heath.svg';
import MarshIcon from './icons/place/location-marsh.svg';
import CoordinateIcon from './icons/place/location-coordinate.svg';
import MudIcon from './icons/place/location-mud.svg';
import ParkIcon from './icons/place/location-park.svg';
import PeakIcon from './icons/place/location-peak.svg';
import ReefIcon from './icons/place/location-reef.svg';
import RvIcon from './icons/place/location-rv-park.svg';
import ScrubIcon from './icons/place/location-scrub.svg';
import SpringIcon from './icons/place/location-spring.svg';
import StoneIcon from './icons/place/location-stone.svg';
import TrailheadIcon from './icons/place/location-trailhead.svg';
import ViewpointIcon from './icons/place/location-viewpoint.svg';
import VolcanoIcon from './icons/place/location-volcano.svg';
import WaterfallIcon from './icons/place/location-waterfall.svg';
import WaterIcon from './icons/place/location-water.svg';
import WoodIcon from './icons/place/location-wood.svg';

/**
 * Match OSM type names to an icon.
 * There should be an icon for for every type in `osm-import/mapping.yml`.
 *
 * DEFAULT_PLACE_ICON= will be used as a last resort fallback if an unknown
 * type is encountered but icons for all types ought to be specified.
 */

export const PLACE_ICONS = {
    alpine_hut: <AttractionIcon />,
    aquarium: <AttractionIcon />,
    arete: <StoneIcon />,
    attraction: <AttractionIcon />,
    bare_rock: <StoneIcon />,
    bay: <WaterIcon />,
    beach: <BeachIcon />,
    bicycle: <TrailheadIcon />,
    bridleway: <TrailheadIcon />,
    camp_site: <CampsiteIcon />,
    cape: <StoneIcon />,
    caravan_site: <RvIcon />,
    cave_entrance: <CaveIcon />,
    chalet: <AttractionIcon />,
    city: <CityIcon />,
    cliff: <StoneIcon />,
    common: <GrasslandIcon />,
    coordinate: <CoordinateIcon />,
    cycleway: <TrailheadIcon />,
    dam: <AttractionIcon />,
    disc_golf_course: <AttractionIcon />,
    dog_park: <DogParkIcon />,
    drystream: <WaterIcon />,
    fell: <FellIcon />,
    fishing: <WaterIcon />,
    foot: <TrailheadIcon />,
    footway: <TrailheadIcon />,
    forest: <ForestIcon />,
    garden: <GardenIcon />,
    geyser: <SpringIcon />,
    glacier: <GlacierIcon />,
    grass: <GrassIcon />,
    grassland: <GrasslandIcon />,
    heath: <HeathIcon />,
    hiking: <TrailheadIcon />,
    hiking_trail: <TrailheadIcon />,
    horse: <AttractionIcon />,
    horse_riding: <AttractionIcon />,
    hot_spring: <SpringIcon />,
    known_route: <TrailheadIcon />,
    locality: <CityIcon />,
    marina: <AttractionIcon />,
    marsh: <MarshIcon />,
    mtb: <TrailheadIcon />,
    mud: <MudIcon />,
    museum: <AttractionIcon />,
    national_park: <ParkIcon alt='national park' />,
    nature_reserve: <ParkIcon alt='nature reserve' />,
    park: <ParkIcon />,
    path: <TrailheadIcon />,
    peak: <PeakIcon />,
    pedestrian: <TrailheadIcon />,
    picnic_site: <AttractionIcon />,
    protected_area: <ParkIcon alt='protected area' />,
    reef: <ReefIcon />,
    ridge: <StoneIcon />,
    river: <WaterIcon />,
    rock: <StoneIcon />,
    saddle: <StoneIcon />,
    sand: <StoneIcon />,
    scree: <StoneIcon />,
    scrub: <ScrubIcon />,
    sinkhole: <StoneIcon />,
    ski: <TrailheadIcon />,
    snowmobile: <TrailheadIcon />,
    spring: <SpringIcon />,
    steps: <TrailheadIcon alt='steps' />,
    stepssteps: <TrailheadIcon />, // handle bad data in source, update import to correct later
    stone: <StoneIcon />,
    summer_camp: <CampsiteIcon />,
    swimming_area: <AttractionIcon />,
    theme_park: <AttractionIcon />,
    track: <TrailheadIcon />,  // handle bad data in source
    tracktrack: <TrailheadIcon />,
    trail: <TrailheadIcon alt='trail' />,
    viewpoint: <ViewpointIcon />,
    volcano: <VolcanoIcon />,
    water: <WaterIcon />,
    waterfall: <WaterfallIcon />,
    wetland: <MarshIcon />,
    wilderness_hut: <AttractionIcon />,
    wildlife_hide: <AttractionIcon />,
    wood: <WoodIcon />,
    zoo: <AttractionIcon />,
};

export const DEFAULT_PLACE_ICON = <CoordinateIcon />;

export * from './colors';
import {
    CONIFER,
    GRANITE_2,
    GRANITE_3,
    GRANITE_5,
    GRANITE_7,
    GRANITE_8,
} from './colors';

export const GRID_UNIT = 4;
export const H3_FONT_SIZE = 16;
export const H4_FONT_SIZE = 15;
export const SMALL_FONT_SIZE = 11;

export const globalStyles = {
    /**
     * Clear default styles by object merging nulls
     */
    clear: {
        background: null,
        backgroundAttachment: null,
        backgroundClip: null,
        backgroundColor: null,
        backgroundImage: null,
        backgroundOrigin: null,
        backgroundPosition: null,
        backgroundRepeat: null,
        backgroundSize: null,
        border: null,
        borderRadius: null,
        borderBox: null,
        boxShadow: null,
        boxSizing: null,
        color: null,
        cursor: null,
        display: null,
        flex: null,
        fontFamily: null,
        fontSize: null,
        fontWeight: null,
        height: null,
        letterSpacing: null,
        left: null,
        lineHeight: null,
        margin: null,
        marginBottom: null,
        marginLeft: null,
        marginRight: null,
        marginTop: null,
        maxHeight: null,
        minHeight: null,
        minWidth: null,
        opacity: null,
        outline: null,
        overflow: null,
        padding: null,
        paddingBottom: null,
        paddingLeft: null,
        paddingRight: null,
        paddingTop: null,
        textAlign: null,
        textDecoration: null,
        textTransform: null,
        top: null,
        transition: null,
        userSelect: null,
        verticalAlign: null,
        whiteSpace: null,
        width: null,
        zIndex: null
    },
    h1: {fontSize: 22.4, fontWeight: 500},
    h2: {fontSize: 18, marginTop: GRID_UNIT * 2, marginBottom: 0, color: CONIFER},
    h3: {fontSize: H3_FONT_SIZE, marginTop: GRID_UNIT * 2, marginBottom: 0, color: GRANITE_2},
    h4: {
        fontSize: H4_FONT_SIZE,
        marginTop: GRID_UNIT * 2,
        marginBottom: 0,
        fontWeight: 500,
        color: GRANITE_3
    },
    bodyText: {
        fontSize: 12,
        color: GRANITE_3,
        lineHeight: '140%'
    },
    bold: {
        fontWeight: 600,
        color: CONIFER
    },
    unstyledList: {listStyle: 'none', paddingLeft: 0},
    verticalAlignIcon: {verticalAlign: 'middle'},
    hr: {
        display: 'block',
        height: 1,
        border: 0,
        backgroundColor: GRANITE_7,
        margin: 0,
        marginTop: GRID_UNIT * 3,
        width: '100%'
    },
    ellipsisText: {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    difficulty: {
        marginLeft: GRID_UNIT * 2,
        paddingTop: 3,
        paddingBottom: 1,
        paddingLeft: GRID_UNIT * 2,
        paddingRight: GRID_UNIT * 2,
        borderRadius: 8,
        fontSize: 9,
        color: GRANITE_8,
        backgroundColor: GRANITE_5,
        textTransform: 'uppercase',
        fontWeight: 100
    }
};

export {default as theme, spacingUnit} from './theme';
